// src/pages/Board.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const AnimatedSection = ({ children, className = '' }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ y: 60, opacity: 0 }}
      animate={inView ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const BoardMember = ({ name, role, description, delay = 0 }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ y: 60, opacity: 0 }}
      animate={inView ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.6, delay }}
      whileHover={{ y: -10 }}
      className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
    >
      <div className="bg-gradient-to-br from-blue-500 to-blue-600 h-48 lg:h-56 flex items-center justify-center text-white">
        <div className="text-6xl">👤</div>
      </div>
      <div className="p-6 lg:p-8">
        <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2 lg:mb-3">{name}</h3>
        <p className="text-blue-600 font-semibold text-lg lg:text-xl mb-4 lg:mb-6">{role}</p>
        <p className="text-gray-600 text-base lg:text-lg leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
};

const Board = () => {
  const boardMembers = [
    {
      name: 'Naa Dr. Saaka Mwinimaana II, Maasinaa',
      role: 'Board Chairman and Founder',
      description: 'Maasinaa, Naa Mwinimaana II (Dr. Mwinimaana) has been Chief of Maasi since July 2016. He is the founder and Board Chairman of The M.A Seidu Education Fund. He is a former executive in the Government of Canada, where he occupied various positions in policy, planning, and communications. He lives in Maasi and now works as a development agent with interest in education, health, water, and sanitation.',
    },
    {
      name: 'Mrs. Halima Saadia Yakubu',
      role: 'Board Member',
      description: 'Mrs. Halima Saadia Yakubu is a former Chief Director of the Ministry of Chieftaincy and Religious Affairs. She is an experienced Civil Servant who has worked in several sectors for many years. Hajia Halima has vast working experience and built networks in her career at the Upper East Regional Coordinating Council, the Northern Regional Coordinating Council, the Brong Ahafo Coordinating Council, the Ministry of Defence, the Ministry of Energy, Ministry of Trade, Ministry of Transport. She also served as Chief Director at the Office of the Senior Minister and the Office of the Presidential Advisor and the Public Sector Reform for Results Secretariat. Hajia Halima holds a bachelor’s degree from the Kwame Nkrumah University of Science and Technology (KNUST) in Social Sciences and a master’s degree in Human Resource Management and Training from Leicester University in the United Kingdom. She currently dedicates her time to supporting vulnerable members of society.',
    },
    {
      name: 'Dr. Solomon Ali Dansieh',
      role: 'Board Member',
      description: 'Dr. Solomon Ali Dansieh is a Senior Lecturer at the Dr. Hilla Limann Technical Unversity, Wa. He is a former Vice Rector and currently, he is the Director of Academic Planning and Quality Assurance of the University. He is an Old Boy of now Wa Senior High School, where he was once Senior Prefect. He is the current Chairman of the Wa Secondary School Old Students’ Association, Wa Municipal Chapter and a member of the Board of Governors of Wa Senior High School.',
    },
    {
      name: 'Hajia Fusata Hamidu',
      role: 'Board Member',
      description: 'Hajia Fusata Hamidu is a retired educationist whose last place of work was the Wa Municipal Education Office in Upper West Region of Ghana. She lives in Wa. She is a Professional Counselor who renders counseling services to those that may need it in her community.',
    },
    {
      name: 'Miss Nasihah Imoru Seidu',
      role: 'Treasurer & Board Member',
      description: 'Miss Nasihah Imoru Seidu completed her Basic Education at Dan-Ibu International School in 1999. Then she proceeded to Bolgatanga Girls Secondary School for her SSCE certification in 2002. She went to Islamic University College to pursue her first degree in Bachelor of Business Administration (Accounting Option) in 2008. She is a level 2 student of the Institute of Chartered Accountants-Ghana. She currently works at Suglo and Associates, an audit firm based in Accra.',
    },
    {
      name: 'Banaangmene Musah Dabilah (Prince)',
      role: 'Secretary & Board Member',
      description: 'Banaangmene Musah Dabilah (Prince) had his secondary school education in now Wa Senior High School. He proceeded to do a Higher National Diploma (HND) in Accountancy in now Dr. Hilla Limann Technical University. He has first degree in Bachelor of Art in Commerce from University of Cape Coast (UCC). He is an accountant and auditor with eight (8) years cumulative experience in company and NGO audits. He worked with Suglo & Associates, a local audit firm in Accra as a Trainee Accountant and rose to Audit Senior Level. He now works with the Northern Electricity Distribution Company (NEDCo). He also works part-time with Maana Consult as Audit Manager.',
    },
    {
      name: 'Haruna Eliyasu',
      role: 'Board Member',
      description: 'Haruna Eliyasu is a Conservation, Agriculture, and Natural Resources Management Practitioner with vast knowlodge in Community Resource Management Areas (CRMA) Hotspot Intervention Areas (HIA) Development, and Landscape governance. He holds BSc. in Agriculture Technology and a Master of Philosophy in Environment and Resource Management Studies from the University of Development Studies (Tamale, Ghana). From 2009 - 2020, Haruna worked for Nature Conservation Research Center in various capacities, contributing to effective governance, natural resources management, and conservation in the Black Volta Basin. Haruna is also a professional teacher, teaching Chemistry, and Integrated Science. As an aluminus of Dan-Ibu International School, Haruna represents the past students on the school management board.',
    },
    {
      name: 'James Suglo',
      role: 'Board Member',
      description: 'Mr. Suglo is a certified public accountant by profession. He has worked many years as an auditor with international organisations and in the private sector. He has extensive experience in financial policy issues, as well as financial statement, compliance, performance and operations audit of multi national companies and not-for-profit organisations. Mr. Suglo did his General Certificate of Education, Ordinary Level, in Wa Secondary School.',
    },
    {
      name: 'Iddrisu Seidu',
      role: 'Board Member',
      description: 'Mr. Iddrisu Seidu is a son of the late Mr. M.A Seidu. He is currently the head and a director of Dan-Ibu International School, founded by Mr. M.A. Seidu. He also ran Al-Mak Construction, a family construction company for many years. Mr. Iddrisu lives with his family in Wa, Upper West Region, Ghana.',
    },
  ];

//   const advisoryBoard = [
//     {
//       name: 'Prof. David Thompson',
//       affiliation: 'Harvard Graduate School of Education',
//       expertise: 'Educational Policy & Reform'
//     },
//     {
//       name: 'Lisa Wang',
//       affiliation: 'Gates Foundation',
//       expertise: 'Global Education Initiatives'
//     },
//     {
//       name: 'Dr. Anthony Green',
//       affiliation: 'Stanford University',
//       expertise: 'Student Development & Psychology'
//     },
//     {
//       name: 'Sarah Martinez',
//       affiliation: 'Google.org',
//       expertise: 'Educational Technology'
//     },
//  ];

  return (
    <div className="max-w-6xl xl:max-w-7xl 2xl:max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      {/* Header */}
      <AnimatedSection className="text-center mb-16 lg:mb-24">
        <h1 className="text-4xl xs:text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-800 mb-6 lg:mb-8">
          Our Board
        </h1>
        <p className="text-xl sm:text-2xl lg:text-3xl text-gray-600 max-w-4xl xl:max-w-5xl mx-auto leading-relaxed">
          Meet the dedicated professionals who guide our mission and ensure we continue 
          to make a meaningful impact in students' lives through strategic leadership and expertise.
        </p>
      </AnimatedSection>

      {/* Board Members Grid */}
      <AnimatedSection className="mb-20 lg:mb-32">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-8 lg:mb-16 text-center">
          Board of Directors
        </h2>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 text-start lg:gap-8">
          {boardMembers.map((member, index) => (
            <BoardMember
              key={index}
              {...member}
              delay={index * 0.1}
            />
          ))}
        </div>
      </AnimatedSection>

      {/* Advisory Board */}
     {/** <AnimatedSection className="bg-gray-50 rounded-2xl lg:rounded-3xl p-8 lg:p-12 xl:p-16">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-8 lg:mb-16 text-center">
          Advisory Board
        </h2>
       
        *  <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {advisoryBoard.map((advisor, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.02 }}
              className="bg-white p-6 lg:p-8 rounded-xl lg:rounded-2xl shadow-md hover:shadow-lg transition-all duration-300"
            >
              <h3 className="text-lg lg:text-xl xl:text-2xl font-semibold text-gray-800 mb-2 lg:mb-3">
                {advisor.name}
              </h3>
              <p className="text-blue-600 text-base lg:text-lg mb-2 lg:mb-3 font-medium">
                {advisor.affiliation}
              </p>
              <p className="text-gray-600 text-sm lg:text-base">
                {advisor.expertise}
              </p>
            </motion.div>
          ))}
        </div>
       
      </AnimatedSection>
       */}
    </div>
  );
};

export default Board;